import { Modal } from '@klear/ui/components/modal'
import { DatePicker } from '@klear/ui/components/date-picker'
import { Label } from '@klear/ui/components/label'
import { Checkbox } from '@klear/ui/components/checkbox'
import { KlearDate } from '@klear/date'
import { useEffect, useMemo, useState } from 'react'
import { trpc } from '@klear/ui/lib/trpc'
import { toast } from '@klear/ui/hooks/use-toast'
import { useOrderEventInvalidate } from '@klear/ui/hooks/use-order-event-invalidate'
import isError from 'lodash/isError'
import { FEE_ENTITY_TYPE_LABEL, type FeeEntityType } from '@klear/constants'

interface MarkFeesPaidModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  offerId: number
  offerIdDisplay: string
}

function feeTypeLabel(type: string): string {
  return FEE_ENTITY_TYPE_LABEL[type as FeeEntityType] ?? type.replace(/_/g, ' ')
}

export function MarkFeesPaidModal({ isOpen, onClose, onSuccess, offerId, offerIdDisplay }: MarkFeesPaidModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [paidAt, setPaidAt] = useState<KlearDate>(KlearDate.today())
  const [selectedFeeIds, setSelectedFeeIds] = useState<Set<number>>(new Set())
  const invalidateOrderEvents = useOrderEventInvalidate()

  const { data: collectableFees = [], isLoading: isLoadingFees } = trpc.collection.listCollectableFees.useQuery(
    { filters: { parentEventId: [offerId] } },
    { enabled: isOpen }
  )

  const collectableFeeIds = useMemo(
    () => collectableFees.map((row) => row.fee.id).filter((id): id is number => id != null),
    [collectableFees]
  )

  useEffect(() => {
    if (!isOpen) {
      setSelectedFeeIds(new Set())
      setPaidAt(KlearDate.today())
      return
    }
    if (collectableFeeIds.length > 0) {
      setSelectedFeeIds(new Set(collectableFeeIds))
    }
  }, [isOpen, collectableFeeIds])

  const markFeesPaidMutation = trpc.collection.markFeesPaidAsOf.useMutation()

  const toggleFee = (feeId: number, checked: boolean) => {
    setSelectedFeeIds((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(feeId)
      } else {
        next.delete(feeId)
      }
      return next
    })
  }

  const handleSubmit = async () => {
    const feeIds = [...selectedFeeIds]
    if (feeIds.length === 0) return

    setIsLoading(true)
    try {
      await markFeesPaidMutation.mutateAsync({
        offerId,
        paidAt: paidAt.toJSON(),
        feeIds
      })
      await invalidateOrderEvents()
      toast({
        title: 'Fees marked as paid',
        description: `${feeIds.length} fee${feeIds.length === 1 ? '' : 's'} on ${offerIdDisplay} marked as paid.`
      })
      onSuccess?.()
      onClose()
    } catch (error) {
      toast({
        title: 'Error',
        description: isError(error) ? error.message : 'Failed to mark fees as paid',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const canSubmit = selectedFeeIds.size > 0 && !isLoading && !isLoadingFees

  return (
    <Modal
      size="md"
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Mark Fees as Paid"
      description={`Select uncollected fees on ${offerIdDisplay} and set the payment date.`}
      onSubmit={handleSubmit}
      primaryText={isLoading ? 'Processing...' : 'Mark Fees Paid'}
      primaryDisabled={!canSubmit}
      secondaryText="Cancel"
      secondaryDisabled={isLoading}
      handleSecondaryAction={onClose}
      handlePrimaryAction={handleSubmit}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Paid At</Label>
          <DatePicker value={paidAt} onChange={(date) => date && setPaidAt(date)} disabled={isLoading} />
        </div>

        <div className="space-y-2">
          <Label>Uncollected Fees</Label>
          {isLoadingFees ? (
            <p className="text-sm text-muted-foreground">Loading fees...</p>
          ) : collectableFees.length === 0 ? (
            <p className="text-sm text-muted-foreground">No uncollected fees on this offer.</p>
          ) : (
            <div className="rounded-md border divide-y max-h-60 overflow-y-auto">
              {collectableFees.map((row) => {
                const feeId = row.fee.id
                if (feeId == null) return null
                return (
                  <label
                    key={feeId}
                    htmlFor={`mark-fees-paid-${feeId}`}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50"
                  >
                    <Checkbox
                      id={`mark-fees-paid-${feeId}`}
                      checked={selectedFeeIds.has(feeId)}
                      onCheckedChange={(checked) => toggleFee(feeId, checked === true)}
                      disabled={isLoading}
                    />
                    <div className="flex flex-1 items-center justify-between gap-2 min-w-0">
                      <span className="text-sm font-medium truncate">{feeTypeLabel(row.fee.type)}</span>
                      <span className="text-sm text-muted-foreground shrink-0">{row.remainingAmount.toString()}</span>
                    </div>
                  </label>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
